import { SetStateAction, useEffect, useRef, useState, useCallback } from 'react';
import {
  View,
  Text,
  Image,
  Dimensions,
  StyleSheet,
  ScrollView,
  Pressable,
  Platform,
  ActivityIndicator,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
const { width, height } = Dimensions.get('window');
import { Entypo, FontAwesome6, MaterialIcons } from '@expo/vector-icons';
import { FontAwesome } from '@expo/vector-icons';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { RootStackParamList } from '../navigation/stackTypes';
import { RouteProp, useFocusEffect } from '@react-navigation/native';
import {
  getDateString,
  getTimeString,
  secondsToHHMMSS,
} from '../components/common/utils/date-time-conversions';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { getPaymentDetails } from '../services/payments-service';
import CreateAccountForm from './CreateAccountForm';
import { UserSessionAttributes } from '../models/user-session-model';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../store';
import { getActiveCharges } from '../store/actions/user-actions';
import SignInOptions from './SignInOptions';
import { isSuccessful } from '../services/api-requests';
import { getOrderById } from '../services/orders-service';
import { OrderAttributes } from '../models/order-model';
import { HeaderWithMenu } from '../components/menu';
import RoleService from '../services/role-service';
import { BottomSheetModalRef } from '../components/BottomSheet';

type ChargeSummaryNavigationProp = NativeStackNavigationProp<
  RootStackParamList,
  'ChargeSummary'
>;
type ChargeSummaryRouteProp = RouteProp<RootStackParamList, 'ChargeSummary'>;

type Props = {
  navigation: ChargeSummaryNavigationProp;
  route: ChargeSummaryRouteProp;
};

const InfoRow = ({
  label,
  value,
  isCurrency = false,
}: {
  label: string;
  value: string;
  isCurrency?: boolean;
}) => (
  <View className="flex flex-row justify-between items-center mt-2 mb-2">
    <Text style={styles.textColor}>{label}</Text>
    <Text className="text-sm font-normal text-white">
      {isCurrency ? (
        <>
          <FontAwesome name="dollar" size={13} color="white" className="mr-1" />
          {value}
        </>
      ) : (
        value
      )}
    </Text>
  </View>
);

export const ChargeSummary = ({ navigation, route }: Props) => {
  const dispatch: AppDispatch = useDispatch();
  // const selectedCharger: ChargerAttributes = useSelector(
  //     (state: RootState) => state.chargers.selectedCharger
  // ) as ChargerAttributes
  //pass the charger in by route,
  //  as this charge summary screen will also be generated from transaction history
  //const charger:ChargerAttributes = route?.params.charger;
  // const [chargerHistory, setChargerHistory] = useState<ChargerHistoryAttributes | null>(null);
  const [order, setOrder] = useState<OrderAttributes | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>('');
  const [pt, setPt] = useState<number>(0);
  const [opt, setOpt] = useState<number>(0);
  const [lastFourCard, setLastFourCard] = useState<string>('');
  const [selectedOption, setSelectedOption] = useState('');
  const [showCreateAccount, setShowCreateAccount] = useState(false);
  const { charger, startTime, endTime, orderId, fromScreen, pendingReconciliation } = route?.params;
  const isHourly = order?.billing_type === 'hourly' || charger?.billing_type === 'hourly';
  const rateUnit = isHourly ? '/hr' : '/kWh';
  const [isReconciling, setIsReconciling] = useState<boolean>(pendingReconciliation === true);
  const [isAwaitingCompletion, setIsAwaitingCompletion] = useState<boolean>(false);
  // Set when the order reached a terminal state other than COMPLETE
  // (e.g. CANCELLED after a timed-out start). Nothing more will arrive, so
  // we must stop polling and stop showing the "finalizing" spinner.
  const [terminalStatus, setTerminalStatus] = useState<string | null>(null);
  const reconciliationPollRef = useRef<NodeJS.Timeout | null>(null);

  // Statuses that still resolve to a receipt; anything else is terminal.
  const isSettlingStatus = (status: string | undefined) =>
    status === 'IN_PROCESS' || status === 'PENDING_RECONCILIATION';

  const handleTerminalOrder = (status: string) => {
    console.log('[ChargeSummary] Order ended without completing:', status);
    setIsReconciling(false);
    setIsAwaitingCompletion(false);
    setTerminalStatus(status);
    dispatch(getActiveCharges());
  };
  const session: UserSessionAttributes | undefined = useSelector(
    (state: RootState) => state.user.session
  );

  // Disable back button when coming from ChargingScreen
  useEffect(() => {
    if (fromScreen === 'ChargingScreen') {
      navigation.setOptions({
        headerLeft: () => null, // This removes the back button
        gestureEnabled: false, // This disables swipe back gesture on iOS
        headerBackVisible: false, // This ensures the back button is not visible
        headerRight: () => (
          <Pressable
            onPress={() => navigation.navigate('MyChargers' as any)}
            style={{ marginRight: 16 }}
          >
            <Text style={{ color: '#39BEB7', fontSize: 16, fontWeight: '600' }}>
              Done
            </Text>
          </Pressable>
        ),
      });
    } else if (fromScreen === 'TransactionHistory') {
      // Ensure back navigation goes to TransactionHistory
      navigation.setOptions({
        headerLeft: () => (
          <Pressable
            onPress={() => navigation.navigate('TransactionHistory' as any)}
            style={{ marginLeft: 16 }}
          >
            <Text style={{ color: '#39BEB7', fontSize: 16, fontWeight: '600' }}>
              ← Back
            </Text>
          </Pressable>
        ),
        gestureEnabled: true,
        headerBackVisible: false, // Hide default back button
        headerRight: undefined,
      });
    } else {
      // Reset to default navigation options when coming from other screens
      navigation.setOptions({
        headerLeft: undefined,
        gestureEnabled: true,
        headerBackVisible: true,
        headerRight: undefined,
      });
    }
  }, [fromScreen, navigation]);

  const transferFee = (order?.pct_fee_rate ?? 0);
  const discount = (order?.discount ?? 0);

  //grab the charger history
  // 'api/charger/chargerHistoryByTime'
  useEffect(() => {
    const getOrder = async () => {
      try {
        const orderResponse = await getOrderById(orderId);
        if (isSuccessful(orderResponse.status) && orderResponse.payload) {
          const fetchedOrder = orderResponse.payload;
          if (fetchedOrder.status === 'COMPLETE') {
            setOrder(fetchedOrder);
            setPt(fetchedOrder.charge?.peak_charge_time ?? 0);
            setOpt(fetchedOrder.charge?.off_peak_charge_time ?? 0);
            setIsReconciling(false);
            setIsAwaitingCompletion(false);
            dispatch(getActiveCharges());
          } else if (fetchedOrder.status === 'PENDING_RECONCILIATION') {
            console.log('[ChargeSummary] Order pending reconciliation (charger offline)');
            setIsReconciling(true);
          } else if (fetchedOrder.status === 'IN_PROCESS') {
            console.log('[ChargeSummary] Order still in process, awaiting completion');
            setIsReconciling(false);
            setIsAwaitingCompletion(true);
          } else {
            // CANCELLED / anything else: terminal. Previously this fell into
            // the "awaiting completion" spinner and polled forever.
            handleTerminalOrder(fetchedOrder.status);
          }
        }
      } catch (err) {
        console.error('[ChargeSummary] Error fetching order:', err);
      }
    };

    const getUserEmail = async () => {
      try {
        const email = await AsyncStorage.getItem('email');
        setUserEmail(email);
      } catch (err) {
        console.error('[ChargeSummary] Error fetching email:', err);
      }
    };

    getOrder();
    getUserEmail();
  }, []);

  useEffect(() => {
    if (order?.is_free_override || order?.pricing_override_discount_type === 'free') return;

    const cardLastFour = async () => {
      try {
        const paymentDetailsResponse = await getPaymentDetails(orderId);
        if (isSuccessful(paymentDetailsResponse.status)) {
          setLastFourCard(paymentDetailsResponse.payload?.lastFour ?? 'unknown');
        } else {
          console.error('[ChargeSummary] Unable to retrieve payment details.');
        }
      } catch (err) {
        console.error('[ChargeSummary] Error fetching payment details:', err);
      }
    };

    cardLastFour();
  }, [orderId, order?.is_free_override, order?.pricing_override_discount_type]);

  // Poll for order completion when the order is not yet finalized.
  // Triggers for both PENDING_RECONCILIATION (charger offline) and IN_PROCESS
  // (order still being finalized). Uses useFocusEffect so polling stops when
  // the user navigates away or backgrounds the app.
  const needsPolling = isReconciling || isAwaitingCompletion;
  useFocusEffect(
    useCallback(() => {
      if (!needsPolling) return;

      const pollForResolution = async () => {
        try {
          const orderResponse = await getOrderById(orderId);
          if (isSuccessful(orderResponse.status) && orderResponse.payload) {
            if (orderResponse.payload.status === 'COMPLETE') {
              setIsReconciling(false);
              setIsAwaitingCompletion(false);
              setOrder(orderResponse.payload);
              setPt(orderResponse.payload.charge?.peak_charge_time ?? 0);
              setOpt(orderResponse.payload.charge?.off_peak_charge_time ?? 0);
              dispatch(getActiveCharges());
            } else if (orderResponse.payload.status === 'PENDING_RECONCILIATION' && !isReconciling) {
              setIsReconciling(true);
              setIsAwaitingCompletion(false);
            } else if (!isSettlingStatus(orderResponse.payload.status)) {
              handleTerminalOrder(orderResponse.payload.status);
            }
          }
        } catch (err) {
          console.error('[ChargeSummary] Error polling for order completion:', err);
        }
      };

      pollForResolution();
      reconciliationPollRef.current = setInterval(pollForResolution, needsPolling && isAwaitingCompletion ? 5000 : 15000);

      return () => {
        if (reconciliationPollRef.current) {
          clearInterval(reconciliationPollRef.current);
          reconciliationPollRef.current = null;
        }
      };
    }, [needsPolling, isReconciling, isAwaitingCompletion, orderId])
  );

  //once we have all the charger history data, parse it for the output
  // useEffect (() => {
  //     //determine peak/off-peak durations and final cost here
  //     // we have all the data from the charger at this point
  //     if (chargerHistory){
  //         setPt(chargerHistory?.peak_charge_time ?? 0);
  //         setOpt(chargerHistory?.off_peak_charge_time ?? 0);
  //     }
  // }, [order]);

  const handleOptionSelect = (value: SetStateAction<string>) => {
    setSelectedOption(value === selectedOption ? '' : value); // Toggle selection
  };

  const handleCreateAccount = (): void => {
    setShowCreateAccount(!showCreateAccount);
  };

  const bottomSheetModalRef = useRef<BottomSheetModalRef>(null);
  const refCreateAccount = useRef<View>(null);
  const refScrollView = useRef<ScrollView | null>(null);
  const OpenBottomSheet = () => {
    if (bottomSheetModalRef.current) {
      bottomSheetModalRef.current?.present();
    }
  };

  useEffect(() => {
    if (showCreateAccount && refScrollView.current && refCreateAccount.current) {
      // Use a timeout to ensure the form has rendered before scrolling
      setTimeout(() => {
        // Use measure to get absolute position on screen
        refCreateAccount.current?.measure((_fx, _fy, _width, _height, _px, pageY) => {
          if (pageY !== undefined) {
            console.log('Form position pageY:', pageY);
            // Scroll to show the top of the form (subtract a bit for padding)
            const scrollPosition = Math.max(0, pageY - 100);
            refScrollView.current?.scrollTo({ y: scrollPosition, animated: true });
          }
        });
      }, 150);
    }
  }, [showCreateAccount]);

  // Fallback to scroll to the end of the ScrollView
  const fallbackScrollToEnd = () => {
    try {
      refScrollView.current?.scrollToEnd({ animated: true });
    } catch (error) {
      console.error('Error in fallback scrolling:', error);
    }
  };

  const handleSignIn = (): void => {
    // use the signin popup
    // TODO: make sure to save this screen and come back to it!
    OpenBottomSheet();
  };

  return (
    <View style={{ flex: 1, backgroundColor: '#1A1D21' }}>
      <SafeAreaView style={styles.safeArea} edges={['top']}>
        <HeaderWithMenu 
          forceHamburger 
          title="Charge Summary" 
          insideSafeArea 
        />
        <View
          style={[
            styles.containerBgColor,
            { flex: 1 },
          ]}
        >

        <ScrollView ref={refScrollView}>
          <View className="p-4">
            <Text className="text-base font-semibold text-white">
              Your Charge Summary
            </Text>

            {isReconciling && (
              <View style={styles.reconciliationBanner}>
                <MaterialIcons name="wifi-off" size={28} color="#FBD644" style={{ marginBottom: 8 }} />
                <Text style={styles.reconciliationTitle}>
                  Finalizing Your Charge
                </Text>
                <Text style={styles.reconciliationText}>
                  The charger was offline when your session ended. Your final charge
                  amount will be calculated once it reconnects. We'll notify you when
                  your receipt is ready.
                </Text>
                <ActivityIndicator size="small" color="#39BEB7" style={{ marginTop: 12 }} />
                <Text style={styles.reconciliationSubtext}>
                  Checking for updates...
                </Text>
              </View>
            )}

            {!isReconciling && isAwaitingCompletion && (
              <View style={styles.reconciliationBanner}>
                <ActivityIndicator size="large" color="#39BEB7" style={{ marginBottom: 8 }} />
                <Text style={styles.reconciliationTitle}>
                  Completing Your Session
                </Text>
                <Text style={styles.reconciliationText}>
                  Your charge is being finalized. This should only take a moment.
                </Text>
              </View>
            )}

            {terminalStatus && (
              <View style={styles.reconciliationBanner}>
                <MaterialIcons name="info-outline" size={28} color="#FBD644" style={{ marginBottom: 8 }} />
                <Text style={styles.reconciliationTitle}>
                  {terminalStatus === 'CANCELLED' ? 'Session Cancelled' : 'Session Ended'}
                </Text>
                <Text style={styles.reconciliationText}>
                  {terminalStatus === 'CANCELLED'
                    ? 'This session was cancelled before it completed. Any payment hold has been released and you have not been charged.'
                    : 'This session has ended. If you believe you were charged incorrectly, please contact support.'}
                </Text>
              </View>
            )}

            <View
              className="flex flex-row gap-3 items-center pb-3 mt-3 ml-0 rounded-2xl"
              style={styles.BgColor1}
            >
              <Image
                source={require('../../assets/RegisterBgImage.png')}
                style={{ width: 100, height: 100, borderRadius: 12 }}
                alt="Station Image"
              />
              <View style={styles.address}>
                <Text className="text-lg font-bold text-white">
                  {`${charger.name}`}
                </Text>
                <Text className="text-sm font-normal" style={styles.textColor}>
                  {`${charger.full_address}`}
                </Text>
              </View>
            </View>

            <View className="mt-4">
              <Text className="mb-3 text-base font-semibold text-white">
                Charger
              </Text>
              <View className="flex flex-row gap-4 items-center">
                <Image
                  source={require('../../assets/chargerType.png')}
                  style={{ width: 32, height: 32 }}
                  alt="Station Image"
                />
                <View>
                  <Text
                    style={styles.textColor2}
                    className="text-xs font-normal"
                  >
                    {`${charger.charger_model} - ${charger.charger_type} J1772 Charger`}
                  </Text>
                  <View className="flex flex-row items-center">
                    <Text className="text-lg font-semibold text-white">
                      {`${charger.charging_capacity} kW`}
                    </Text>
                    <Entypo name="dot-single" size={24} color="#8D8E90" />
                    <Text style={styles.textColor}>power</Text>
                  </View>
                </View>
              </View>
              <View className="flex flex-row justify-between items-center mt-3">
                <Text className="text-base font-semibold text-white">
                  Peak rate ({rateUnit})
                </Text>
                <Text className="text-base font-semibold text-white">
                  <FontAwesome
                    name="dollar"
                    size={15}
                    color="white"
                    className="mr-1"
                  />
                  {order?.price_basis_peak !== undefined
                    ? `${order?.price_basis_peak.toFixed(2)}`
                    : 'Waiting'}
                </Text>
                <Text className="text-base font-semibold text-white">
                  Off-Peak rate ({rateUnit})
                </Text>
                <Text className="text-base font-semibold text-white">
                  <FontAwesome
                    name="dollar"
                    size={15}
                    color="white"
                    className="mr-1"
                  />
                  {order?.price_basis_off_peak !== undefined
                    ? `${order?.price_basis_off_peak.toFixed(2)}`
                    : 'Waiting'}
                </Text>
              </View>
            </View>

            {order?.pricing_override_type && (
              <View style={{ backgroundColor: '#292D33', borderRadius: 10, padding: 12, marginHorizontal: 16, marginTop: 12, flexDirection: 'row', alignItems: 'center' }}>
                <MaterialIcons
                  name={order.pricing_override_type === 'group' ? 'group' : 'confirmation-number'}
                  size={20}
                  color="#40BFB4"
                  style={{ marginRight: 10 }}
                />
                <View style={{ flex: 1 }}>
                  <Text style={{ color: '#FFFFFF', fontSize: 13, fontWeight: '600' }}>
                    {order.pricing_override_discount_type === 'free' ? 'Free Charging' :
                     order.pricing_override_discount_type === 'percentage' ? `${order.pricing_override_discount_value}% Discount` :
                     order.pricing_override_discount_type === 'flat_rate' ? `Flat Rate: $${order.pricing_override_discount_value?.toFixed(3)}${rateUnit}` :
                     'Special Pricing'}
                  </Text>
                  <Text style={{ color: '#8D8E90', fontSize: 11, marginTop: 2 }}>
                    via {order.pricing_override_type === 'group' ? 'Pricing Group' : 'Pricing Code'}: {order.pricing_override_source_name}
                  </Text>
                </View>
              </View>
            )}

            <View style={styles.line} />

            {order && !isReconciling && !isAwaitingCompletion && (
              <View>
                <View
                  className="flex flex-row justify-between items-center p-4 mt-2 mb-2 rounded-full"
                  style={styles.BgColor1}
                >
                  <Text style={styles.textColor}>Your MeanderEV Plan</Text>
                  <Text className="text-sm font-normal text-white">
                    {order.plan.name}
                  </Text>
                </View>

                {(process.env.REACT_NATIVE_ENV === 'stage' ||
                  process.env.REACT_NATIVE_ENV === 'development') && (
                  <View
                    className="flex flex-row justify-between items-center p-4 mt-2 mb-2 rounded-full"
                    style={styles.BgColor1}
                  >
                    <Text style={styles.textColor}>Order Id:</Text>
                    <Text className="text-sm font-normal text-white">
                      {order.id}
                    </Text>
                  </View>
                )}

                <InfoRow
                  label="Booking Date"
                  value={getDateString(new Date(startTime))}
                />
                <InfoRow
                  label="Time of Arrival"
                  value={getTimeString(new Date(startTime))}
                />
                <InfoRow
                  label="Total Charging Duration"
                  value={secondsToHHMMSS(pt + opt)}
                />
                {isHourly ? (
                  <>
                    <InfoRow
                      label="Peak Rate Time"
                      value={secondsToHHMMSS(order?.charge.peak_charge_time ?? 0)}
                    />
                    <InfoRow
                      label="Off-Peak Rate Time"
                      value={secondsToHHMMSS(order?.charge.off_peak_charge_time ?? 0)}
                    />
                  </>
                ) : (
                  <>
                    <InfoRow
                      label="Peak Rate kWh Usage"
                      value={`${(order?.charge.peak_kwh ?? 0).toFixed(2)} kWh`}
                    />
                    <InfoRow
                      label="Off-Peak Rate kWh Usage"
                      value={`${(order?.charge.off_peak_kwh ?? 0).toFixed(2)} kWh`}
                    />
                  </>
                )}
                {discount > 0 ? (
                  <>
                    <InfoRow
                      label="Charging Cost (before discount)"
                      value={((order.kwhPrice ?? 0) + discount).toFixed(2)}
                      isCurrency
                    />
                    <View className="flex flex-row justify-between items-center mt-2 mb-2">
                      <Text style={{ color: '#39BEB7', fontWeight: '600' }}>Discount</Text>
                      <Text style={{ color: '#39BEB7', fontWeight: '600', fontSize: 14 }}>
                        -<FontAwesome name="dollar" size={13} color="#39BEB7" className="mr-1" />
                        {discount.toFixed(2)}
                      </Text>
                    </View>
                    <InfoRow
                      label="Charging Cost"
                      value={(order.kwhPrice ?? 0).toFixed(2)}
                      isCurrency
                    />
                  </>
                ) : (
                  <InfoRow
                    label="Charging Cost"
                    value={(order.kwhPrice ?? NaN).toFixed(2)}
                    isCurrency
                  />
                )}
                <InfoRow
                  label="Tax on Charge"
                  value={order.tax?.toFixed(2) ?? NaN}
                  isCurrency
                />
                <InfoRow
                  label="Transaction Fee"
                  value={((order?.transaction_fee_applied ?? 0)).toFixed(2)}
                  isCurrency
                />

                {transferFee > 0 && (
                  <InfoRow
                    label={`Transfer Fee [${(transferFee * 100).toFixed(0)}%]`}
                    value={((order?.pct_fee ?? 0)).toFixed(2)}
                    isCurrency
                  />
                )}

                <View
                  className="flex flex-row justify-between items-center p-4 mt-2 mb-2 rounded-full"
                  style={styles.BgColor1}
                >
                  <Text style={styles.textColor}>Total cost</Text>
                  <Text className="text-sm font-normal text-white">
                    <FontAwesome
                      name="dollar"
                      size={13}
                      color="white"
                      className="mr-1"
                    />
                    {(order?.total_price ?? NaN).toFixed(2) ?? 0}
                  </Text>
                </View>
              </View>
            )}

            {!order?.is_free_override && order?.pricing_override_discount_type !== 'free' && (
              <View className="mt-1">
                <Text className="text-base font-semibold text-white">
                  Payment Method
                </Text>
                <View className="flex flex-row gap-4 items-center mt-1 mb-1">
                  <Image
                    source={require('../../assets/wallet.png')}
                    style={{ width: 32, height: 32 }}
                    alt="Station Image"
                  />
                  <Text className="text-lg font-semibold text-white">{`Card ending in ${lastFourCard}`}</Text>
                </View>
              </View>
            )}

            <View style={styles.line} />
            {(!userEmail || RoleService.isGuest(session)) && !showCreateAccount && (
              <View style={styles.authPromptContainer}>
                <Text style={styles.authPromptText}>
                  Want to save your charging history?
                </Text>
                <View style={styles.authButtonsContainer}>
                  <Pressable
                    style={[styles.authButton, styles.createAccountButton, { marginRight: 6 }]}
                    onPress={() => {
                      handleCreateAccount();
                      handleOptionSelect('checked');
                    }}
                  >
                    <Text style={styles.authButtonText}>Create an Account</Text>
                  </Pressable>
                  <Pressable
                    style={[styles.authButton, styles.signInButton, { marginLeft: 6 }]}
                    onPress={handleSignIn}
                  >
                    <Text style={styles.authButtonText}>Log In</Text>
                  </Pressable>
                </View>
              </View>
            )}
            {showCreateAccount && (
              <View style={{ backgroundColor: '#1A1D21' }}>
                {/* Back button to return to auth prompt */}
                <View style={styles.backButtonContainer}>
                  <Pressable
                    style={styles.backButton}
                    onPress={() => {
                      handleCreateAccount();
                      handleOptionSelect('');
                    }}
                  >
                    <Text style={styles.backButtonText}>← Back to Log In Options</Text>
                  </Pressable>
                </View>
                <View ref={refCreateAccount}>
                  <CreateAccountForm
                    formEmail=""
                    handleCreateAccount={handleCreateAccount}
                    navigation={undefined}
                    transferOrderId={orderId}
                  />
                </View>
              </View>
            )}
          </View>
        </ScrollView>
        <SignInOptions
          navigation={navigation}
          bottomSheetModalRef={bottomSheetModalRef}
          destination={'ChargeSummary'}
          orderId={orderId}
          transferOrderOnSignIn={true}
        />
        </View>
      </SafeAreaView>
    </View>
  );
};

export default ChargeSummary;

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#1A1D21',
  },
  containerBgColor: {
    backgroundColor: '#1A1D21',
  },
  BgColor1: {
    backgroundColor: '#292D33',
  },
  BgColor2: {
    backgroundColor: '#234D4E',
  },
  BgColor3: {
    backgroundColor: '#E8E8E9',
  },
  BgColor4: {
    backgroundColor: '#4F7F8B',
  },
  line: {
    borderBottomColor: '#636363',
    borderBottomWidth: 1,
    borderStyle: 'solid',
    marginVertical: 20,
  },
  textColor: {
    color: '#8D8E90',
  },
  textColor2: {
    color: '#E8E8E9',
  },
  textColor3: {
    color: '#D1D2D3',
  },
  textColorTeal: {
    color: '#39BEB7',
  },
  signInColor: {
    color: '#39BEB7',
  },
  signInText: {
    fontWeight: 'bold',
    textDecorationLine: 'underline',
  },
  address: {
    width: width <= 430 ? width * 0.5 : 250,
  },
  authPromptContainer: {
    marginTop: 20,
    marginBottom: 20,
    paddingHorizontal: 16,
    alignItems: 'center',
  },
  authPromptText: {
    color: '#E8E8E9',
    fontSize: 16,
    fontWeight: '600',
    textAlign: 'center',
    marginBottom: 16,
  },
  authButtonsContainer: {
    flexDirection: 'row',
    width: '100%',
    justifyContent: 'center',
  },
  authButton: {
    flex: 1,
    maxWidth: 160,
    paddingVertical: 14,
    paddingHorizontal: 20,
    borderRadius: 25,
    alignItems: 'center',
    justifyContent: 'center',
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  createAccountButton: {
    backgroundColor: '#39BEB7',
  },
  signInButton: {
    backgroundColor: 'transparent',
    borderWidth: 2,
    borderColor: '#39BEB7',
  },
  authButtonText: {
    color: '#FFFFFF',
    fontSize: 15,
    fontWeight: '600',
  },
  backButtonContainer: {
    backgroundColor: '#1A1D21',
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#292D33',
  },
  backButton: {
    paddingVertical: 10,
    paddingHorizontal: 16,
    backgroundColor: '#292D33',
    borderRadius: 8,
    alignSelf: 'flex-start',
    borderWidth: 1,
    borderColor: '#39BEB7',
  },
  backButtonText: {
    color: '#39BEB7',
    fontSize: 16,
    fontWeight: '600',
  },
  reconciliationBanner: {
    backgroundColor: '#292D33',
    borderRadius: 16,
    padding: 24,
    marginTop: 16,
    marginBottom: 8,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#FBD64440',
  },
  reconciliationTitle: {
    color: '#FBD644',
    fontSize: 18,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 8,
  },
  reconciliationText: {
    color: '#E8E8E9',
    fontSize: 14,
    lineHeight: 20,
    textAlign: 'center',
  },
  reconciliationSubtext: {
    color: '#8D8E90',
    fontSize: 12,
    marginTop: 6,
  },
});
